import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { type DataTableColumn, DataTable } from '@/components/DataTable';
import { FilterPills, nonEmptyPills, withCount } from '@/components/FilterPills';
import { PageHeader, PageShell } from '@/components/PageShell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useDebouncedSearchParam, useSetSearchParam } from '@/lib/searchParams';
import { useServerSort } from '@/lib/tableSort';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLoadMore } from '@rock/db-admin';
import {
    allBrandsState,
    allEquipmentTypesState,
    MODEL_BRAND_REVIEW_LIMIT,
    modelStatusCountsState,
    modelsNeedingBrandReviewState,
    modelsState,
} from '@/state';
import { useAsyncAtomValue, useFreshOnMountAtomValue } from '@/state/loadableHooks';
import type { Brand, BrandId, Model } from '@rock/types';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DOC_STATUS_FILTERS, DOC_STATUS_FILTER_LABELS, DOC_STATUS_META, type DocStatusFilter } from './documentStatus';
import { AddModelDialog } from './AddModelDialog';
import { brandsById } from './modelBrand';
import { ModelBrandCell } from './ModelBrandCell';

const ModelsListPage: FC = () => {
    const {
        data: models,
        isLoading,
        hasError,
        hasMore,
        loadMore,
        filter,
        setFilter,
        sort,
        setSort,
    } = useLoadMore(modelsState);
    const [addOpen, setAddOpen] = useState(false);
    const brands = useAsyncAtomValue(allBrandsState, []);
    const brandLookup = useMemo(() => brandsById(brands), [brands]);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const typeTitles = useMemo(() => new Map(types.map((type) => [type.id, type.title])), [types]);
    // Filters live in the URL so they survive the row-click → details → back round trip.
    const [searchParams] = useSearchParams();
    const statusParam = searchParams.get('status');
    const manualFilter: DocStatusFilter = DOC_STATUS_FILTERS.find((status) => status === statusParam) ?? 'all';
    // Brand review is the one view with its own complete server query — a queue silently truncated to the
    // loaded page would be worse than no queue. The atom is keyed on `isBrandReview` so the hook mounts
    // unconditionally but only subscribes while the view is open.
    const isBrandReview = manualFilter === 'brand-review';
    const brandReviewModels = useAsyncAtomValue(modelsNeedingBrandReviewState(isBrandReview), []);
    const searchQuery = searchParams.get('q') ?? '';
    const setSearchParam = useSetSearchParam();
    const setManualFilter = (status: DocStatusFilter) =>
        setSearchParam('status', status === 'all' ? undefined : status);
    const { input: searchInput, onChange: handleSearchChange } = useDebouncedSearchParam('q');
    // Counts cover the whole catalog, so a search makes them wrong for what's on screen.
    const { value: catalogCounts } = useFreshOnMountAtomValue(modelStatusCountsState);
    const statusCounts = searchQuery ? undefined : catalogCounts;
    const statusPills = nonEmptyPills(
        DOC_STATUS_FILTERS.filter((status) => status !== 'all'),
        DOC_STATUS_FILTER_LABELS,
        statusCounts,
        manualFilter === 'all' ? undefined : manualFilter,
    );

    // Both dimensions drive the server-side query and the URL is the source of truth for both, so they
    // sync together — `setFilter` REPLACES the filter object, and a per-field update would wipe the other.
    useEffect(() => {
        if (filter.namePrefix !== searchQuery || filter.status !== manualFilter) {
            setFilter({ namePrefix: searchQuery, status: manualFilter });
        }
    }, [searchQuery, manualFilter, filter, setFilter]);

    // The query already selected the status (archived models carry `docStatus: 'archived'`, so every
    // other filter excludes tombstones for free). The unfiltered view is the one that still needs a pass:
    // Firestore has no `!=` that composes with the model-number prefix range.
    const filteredModels = useMemo(() => {
        if (isBrandReview) {
            return brandReviewModels.filter((model) => !model.archivedAt);
        }
        if (manualFilter === 'all') {
            return models.filter((model) => !model.archivedAt);
        }
        return models;
    }, [models, brandReviewModels, isBrandReview, manualFilter]);

    const columns = useMemo(() => modelColumns(brandLookup, typeTitles), [brandLookup, typeTitles]);

    // The review queue is a different query entirely (ordered by the pending suggestion), and a prefix
    // search pins Firestore's ordering to the model number — in both cases the other headers would be
    // lying about what they'd do.
    const sortDisabledReason = (column: DataTableColumn<Model>) => {
        if (isBrandReview) return 'Sorting is unavailable in the review queue.';
        if (searchQuery && column.sortField !== 'modelCanonical') {
            return 'Sorting by this column is unavailable while searching — the search pins the order to Model.';
        }
        return undefined;
    };

    const { sort: tableSort, setSort: setTableSort } = useServerSort(columns, { sort, setSort }, sortDisabledReason);

    return (
        <PageShell>
            <PageHeader
                title="Equipment models"
                description="Review the catalog of models that Rock uses when matching equipment details."
            >
                <Input
                    // The review queue is its own query and ignores the prefix filter; leaving the
                    // box live would silently do nothing.
                    placeholder={isBrandReview ? 'Search unavailable in review queue' : 'Search models…'}
                    value={isBrandReview ? '' : searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    disabled={isBrandReview}
                    className="w-[220px]"
                />
                <Select value={manualFilter} onValueChange={(value) => setManualFilter(value as DocStatusFilter)}>
                    <SelectTrigger className="w-[220px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {DOC_STATUS_FILTERS.map((status) => (
                            <SelectItem key={status} value={status}>
                                {withCount(DOC_STATUS_FILTER_LABELS[status], statusCounts?.[status])}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button onClick={() => setAddOpen(true)}>Add model</Button>
            </PageHeader>
            {addOpen && <AddModelDialog open onOpenChange={setAddOpen} />}
            {!searchQuery && (
                <div className="rounded border bg-background p-3">
                    <FilterPills
                        label="Status"
                        items={statusPills}
                        selected={manualFilter === 'all' ? undefined : manualFilter}
                        allCount={statusCounts?.all}
                        onSelect={(key) =>
                            setManualFilter(DOC_STATUS_FILTERS.find((status) => status === key) ?? 'all')
                        }
                    />
                </div>
            )}
            {filteredModels.length === 0 ? (
                <Card>
                    <CardHeader>
                        {/* Status now selects server-side, so changing it refetches — without this the
                                gap renders "no matches", which is a claim the page can't make yet. */}
                        <CardTitle>
                            {hasError ? 'Couldn’t load models' : isLoading ? 'Loading models…' : 'No models found'}
                        </CardTitle>
                        <CardDescription>
                            {hasError
                                ? 'You may not have permission to view this page.'
                                : isLoading
                                  ? 'Fetching the model catalog.'
                                  : isBrandReview
                                    ? 'Nothing awaiting brand review — every model either has a confirmed brand or no proposal.'
                                    : manualFilter === 'all' && !searchQuery
                                      ? 'Once models are synced into the catalog they will appear in this list.'
                                      : 'No models match the selected filters.'}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>{isBrandReview ? 'Needs brand review' : 'Models'}</CardTitle>
                        <CardDescription>
                            {isBrandReview
                                ? `${brandReviewSummary(filteredModels.length)} with a proposed brand nobody has confirmed. Open one to confirm or dismiss it.`
                                : 'Tap any row to inspect the model details.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <DataTable
                            columns={columns}
                            rows={filteredModels}
                            rowKey={(model) => model.id}
                            rowHref={(model) => `/products/models/${encodeURIComponent(model.id)}`}
                            sort={tableSort}
                            onSortChange={setTableSort}
                            sortDisabledReason={sortDisabledReason}
                        />
                        {hasMore && !isBrandReview && (
                            <div className="mt-4 flex justify-center">
                                <Button variant="outline" onClick={() => loadMore()}>
                                    Load More
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </PageShell>
    );
};

/**
 * Sortable columns are the ones a STORED field backs and can order meaningfully: the model number and
 * the update stamp. Brand is stored as an opaque `brandId` (ordering by it would order by id, not by
 * name), Type would need a composite index beside every status filter, and Variant and Spellings are
 * optional/derived — each needs something denormalized before it could sort. Documents is stored, but
 * ordering an enum alphabetically puts `archived` first, and the unfiltered view drops archived models
 * client-side — a whole page of nothing. Filter by status instead; that's the same question, answered by
 * a query that can actually serve it.
 */
function modelColumns(brandLookup: Map<BrandId, Brand>, typeTitles: Map<string, string>): DataTableColumn<Model>[] {
    return [
        {
            key: 'brand',
            header: 'Brand',
            className: 'min-w-[160px]',
            cellClassName: 'font-medium',
            cell: (model) => <ModelBrandCell model={model} brandLookup={brandLookup} />,
        },
        {
            key: 'model',
            header: 'Model',
            className: 'min-w-[160px]',
            cellClassName: 'font-medium',
            sortField: 'modelCanonical',
            cell: (model) => model.modelCanonical,
        },
        {
            key: 'type',
            header: 'Type',
            className: 'min-w-[140px]',
            cell: (model) => <EquipmentTypeBadge typeId={model.typeId} title={typeTitles.get(model.typeId)} />,
        },
        {
            key: 'variant',
            header: 'Variant',
            className: 'min-w-[120px]',
            cell: (model) => model.variantKey ?? '—',
        },
        {
            key: 'documents',
            header: 'Documents',
            className: 'min-w-[140px]',
            cell: (model) => <DocumentsCell model={model} />,
        },
        {
            key: 'spellings',
            header: 'Spellings',
            className: 'min-w-[120px] text-right',
            cell: (model) => model.spellings.length,
        },
        {
            key: 'updatedAt',
            header: 'Last updated',
            className: 'min-w-[200px]',
            sortField: 'updatedAt',
            sortFirst: 'desc',
            cell: (model) => formatTimestamp(model.updatedAt),
        },
    ];
}

/** Absent only until the rollup's first stamp lands (a model created seconds ago, or one the backfill hasn't reached). */
const DocumentsCell: FC<{ model: Model }> = ({ model }) => {
    const meta = model.docStatus ? DOC_STATUS_META[model.docStatus] : undefined;
    if (!meta) {
        return <span className="text-xs text-muted-foreground">…</span>;
    }
    return (
        <Badge variant={meta.variant} className={meta.className}>
            {meta.label}
        </Badge>
    );
};

/** Honest about the cap: a queue at the limit is showing a page, not a total. */
function brandReviewSummary(shown: number): string {
    return shown >= MODEL_BRAND_REVIEW_LIMIT ? `First ${MODEL_BRAND_REVIEW_LIMIT} models` : `${shown} model(s)`;
}

function formatTimestamp(timestamp?: string | number | null) {
    if (!timestamp) {
        return '—';
    }

    try {
        const value = typeof timestamp === 'number' ? timestamp : Date.parse(timestamp);
        if (Number.isNaN(value)) {
            return '—';
        }
        return new Date(value).toLocaleString();
    } catch {
        return '—';
    }
}

export default ModelsListPage;
